import { Avatar as MuiAvatar, Box } from '@mui/material';
import PropTypes from 'prop-types';
import PhoneIcon from '@mui/icons-material/Phone';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import { useSelector } from 'react-redux';
import AvatarBubble from '../../atoms/AvatarBubble/AvatarBubble';
import { selectChatAllowed, selectShowGravatar } from '../../../state/configs/configsSlice';

function Avatar({
  avatarStyle,
  chatPresence,
  gravatar,
  initials,
  presence,
  size,
  showPresence,
  style,
}) {
  let sizeStyles;
  let leftBubbleStyles;
  let rightBubbleStyles;
  if (size === 'small') {
    sizeStyles = { fontSize: 15, width: 24, height: 24 };
    leftBubbleStyles = { bottom: -4, left: -6 };
    rightBubbleStyles = { bottom: -4, right: -6 };
  } else if (size === 'small-medium') {
    sizeStyles = { width: 32, height: 32 };
    leftBubbleStyles = { bottom: -4, left: -6 };
    rightBubbleStyles = { bottom: -4, right: -6 };
  } else if (size === 'medium-small') {
    sizeStyles = { width: 40, height: 40 };
    leftBubbleStyles = { bottom: -4, left: -6 };
    rightBubbleStyles = { bottom: -4, right: -6 };
  } else if (size === 'contact-chip') {
    sizeStyles = { fontSize: 15, width: 28, height: 28 };
    leftBubbleStyles = { bottom: -4, left: -6 };
    rightBubbleStyles = { bottom: -4, right: -6 };
  } else if (size === 'medium') {
    sizeStyles = { width: 48, height: 48 };
    leftBubbleStyles = { bottom: -4, left: -6 };
    rightBubbleStyles = { bottom: -4, right: -6 };
  } else if (size === 'large') {
    sizeStyles = { width: 56, height: 56 };
    leftBubbleStyles = { bottom: -4, left: -6 };
    rightBubbleStyles = { bottom: -4, right: -6 };
  } else if (size === 'extra-large') {
    sizeStyles = { width: 72, height: 72 };
    leftBubbleStyles = { bottom: 0, left: 2 };
    rightBubbleStyles = { bottom: 0, right: 2 };
  }

  if (!gravatar && !initials && size !== 'contact-chip') {
    sizeStyles = {
      border: '1px solid #bbb',
      width: sizeStyles.width - 1,
      height: sizeStyles.height - 1,
    };
  }

  const chatAllowed = useSelector(selectChatAllowed);
  const showGravatar = useSelector(selectShowGravatar);

  let PresenceIcon = PhoneIcon;
  let presenceColor = 'red.main';
  if (presence === 'open') {
    presenceColor = 'green.light';
  } else if (presence === 'inactive') {
    presenceColor = 'grey.50';
  } else if (presence === 'closed') {
    PresenceIcon = DoNotDisturbIcon;
  }

  // Only request a remote avatar when there is a hash to look up. Without a hash
  // there is nothing to fetch, so fall back to the initials or to the local
  // placeholder MuiAvatar renders on its own instead of asking gravatar for a
  // generic image. The URL is kept free of per-session values so the browser can
  // serve repeat views, and misses, from cache.
  let gravatarUrl = null;
  if (gravatar && showGravatar) {
    gravatarUrl = `https://www.gravatar.com/avatar/${gravatar}?s=${sizeStyles.width}&d=404`;
  }

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }} style={style}>
      <MuiAvatar
        alt="Avatar"
        src={gravatarUrl}
        sx={{ ...sizeStyles, ...avatarStyle }}
      >
        {initials}
      </MuiAvatar>
      {chatAllowed && showPresence && chatPresence && (
        <AvatarBubble
          Icon={ChatBubbleIcon}
          sx={{ ...leftBubbleStyles, color: '#FFF', backgroundColor: '#0072BC' }}
          size={size}
        />
      )}
      {showPresence && presence && presence !== 'inactive' && (
        <AvatarBubble
          Icon={PresenceIcon}
          sx={{ ...rightBubbleStyles, backgroundColor: presenceColor }}
          size={size}
        />
      )}
    </Box>
  );
}

Avatar.defaultProps = {
  avatarStyle: {},
  chatPresence: false,
  gravatar: undefined,
  initials: '',
  size: 'medium',
  presence: '',
  showPresence: true,
  style: undefined,
};

Avatar.propTypes = {
  avatarStyle: PropTypes.shape({}),
  chatPresence: PropTypes.bool,
  gravatar: PropTypes.string,
  initials: PropTypes.string,
  presence: PropTypes.string,
  showPresence: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'contact-chip', 'small-medium', 'medium-small', 'medium', 'large', 'extra-large']),
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default Avatar;
